import { z } from 'zod';
import { TIMEFRAMES } from './timeframe.js';
import { storedWeekday } from './timeWindow.js';
export const knotStateSchema = z.enum(['live', 'hit']);
/**
 * Timeframes knots are read on.
 *
 * M1, M5 and M30 are excluded. A knot is the midpoint of a candle's body, so
 * it means as much as that body does — and a one-minute body is frequently a
 * couple of ticks of noise. At that size the midpoint is not a level anyone
 * defends; it is an artefact of where the minute happened to close, and
 * thousands of them bury the ones that matter on the timeframes actually
 * traded. Five-minute bodies are small enough to read the same way.
 *
 * M30 is dropped for a different reason: its bodies are real, but the level
 * it produces is nearly always one H1 already shows, so it lengthened the
 * list without adding anything to read.
 */
export const KNOT_TIMEFRAMES = TIMEFRAMES.filter((tf) => tf !== 'M1' && tf !== 'M5' && tf !== 'M30');
/**
 * How far back knots are read, per timeframe.
 *
 * Knots are detected over exactly the window the chart draws, so this single
 * number sets both the history on screen and the history counted — raising one
 * without the other would draw levels formed by candles that are not there.
 *
 * The counts are chosen for the CALENDAR reach they give on gold, not for a
 * round number of bars, because a flat bar count means wildly different spans:
 * 1,500 bars was three weeks of M15 and nearly six years of D1.
 *
 *   M15   ~6 months      H4/H4_x  ~5 years
 *   H1    ~2 years       D1/W1    the whole history there is
 *
 * M1, M5 and M30 are not read by this module (see KNOT_TIMEFRAMES); their
 * entries exist because the route accepts any timeframe and a caller asking
 * for one directly should still get a sane window rather than a crash.
 */
export const KNOT_SCAN_BARS = {
    M1: 7_000,
    M5: 8_000,
    M15: 12_000,
    M30: 8_000,
    H1: 12_000,
    H4: 8_000,
    H4_1: 8_000,
    H4_2: 8_000,
    H4_3: 8_000,
    D1: 4_500,
    W1: 1_000,
};
export const DEFAULT_KNOT_TOLERANCE_PCT = 0.03;
/**
 * Default tolerance per timeframe, where it differs from the general one.
 *
 * W1 gets a wider 10% band: a weekly body is tens of dollars on gold, and a
 * close landing within its middle 6% is so rare that weekly knots were almost
 * never taken at the general default.
 */
export const KNOT_TOLERANCE_BY_TIMEFRAME = {
    W1: 0.1,
};
export function defaultKnotTolerance(timeframe) {
    return KNOT_TOLERANCE_BY_TIMEFRAME[timeframe] ?? DEFAULT_KNOT_TOLERANCE_PCT;
}
export const DEFAULT_KNOT_MAX_AGE = 200;
/**
 * Find every knot in a series and the close that consumed it, if any.
 *
 * Knots are formed by every qualifying candle and matched against later closes
 * in order, so the FIRST close to land inside the band takes it. A knot already
 * taken is not re-tested — the level did its job once, and counting a second
 * visit would double-count the same behaviour.
 *
 * A take only counts if the NEXT candle closes the opposite colour: a bullish
 * candle closing on the knot must be followed by a bearish one, a bearish by a
 * bullish. That reversal is what shows the level actually turned price. When
 * the next candle is the same colour, or a doji — or the taking candle is
 * itself a doji, which has no colour to oppose — the knot is not valid and is
 * dropped from the result entirely: the level is used up, but it is neither
 * a live level nor a take.
 *
 * Until the confirming candle has closed the take is undecided: the knot is
 * still live, with `pending` describing the close that is waiting on it.
 */
/** Saturday or Sunday on the instrument's own stored clock. */
function isWeekendBar(storedSeconds) {
    const day = storedWeekday(storedSeconds);
    return day === 0 || day === 6;
}
export function detectKnots(candles, opts = {}) {
    const tolerancePct = opts.tolerancePct ?? DEFAULT_KNOT_TOLERANCE_PCT;
    const maxAge = opts.maxAgeBars ?? DEFAULT_KNOT_MAX_AGE;
    const minBody = opts.minBody ?? 0;
    const tradesWeekends = opts.tradesWeekends ?? true;
    // -1 when every bar is closed, so the comparisons below never match.
    const forming = opts.lastBarIsForming && candles.length > 0 ? candles.length - 1 : -1;
    const out = [];
    for (let i = 0; i < candles.length; i++) {
        if (i === forming)
            continue;
        const c = candles[i];
        const bodyHigh = Math.max(c.open, c.close);
        const bodyLow = Math.min(c.open, c.close);
        const body = bodyHigh - bodyLow;
        if (body <= 0 || body < minBody)
            continue;
        const level = (c.open + c.close) / 2;
        const tolerance = body * tolerancePct;
        const knot = {
            time: c.time,
            level,
            bodyHigh,
            bodyLow,
            direction: c.close > c.open ? 'up' : c.close < c.open ? 'down' : 'flat',
            tolerance,
            hit: null,
            pending: null,
        };
        const last = Math.min(candles.length - 1, i + maxAge);
        let invalid = false;
        for (let j = i + 1; j <= last; j++) {
            if (j === forming)
                continue;
            const bar = candles[j];
            if (Math.abs(bar.close - level) > tolerance)
                continue;
            const candle = direction(bar);
            // A doji has no colour for the next candle to oppose.
            if (candle === 'flat') {
                invalid = true;
                break;
            }
            /*
             * The candle after the take decides it — but a weekend bar is not that
             * candle. See `tradesWeekends`: on an instrument that stops for the
             * weekend, stepping over those leaves the next real session to confirm
             * the take or refuse it.
             *
             * Not closed yet (end of the series, or still forming): undecided, so
             * the knot stays live.
             */
            let k = j + 1;
            if (!tradesWeekends) {
                while (k < candles.length && isWeekendBar(candles[k].time))
                    k++;
            }
            if (k >= candles.length || k === forming) {
                knot.pending = {
                    time: bar.time,
                    close: bar.close,
                    candle,
                    needs: candle === 'up' ? 'down' : 'up',
                };
                break;
            }
            const next = direction(candles[k]);
            const opposite = (candle === 'up' && next === 'down') || (candle === 'down' && next === 'up');
            if (!opposite) {
                invalid = true;
                break;
            }
            // Side is judged by the previous close, which is where price was coming
            // from. Using this bar's open would misread a bar that gapped across.
            const prevClose = candles[j - 1].close;
            knot.hit = {
                time: bar.time,
                close: bar.close,
                barsLater: j - i,
                from: prevClose >= level ? 'above' : 'below',
                candle,
                confirmTime: candles[k].time,
                // Inverted deliberately — see the field's documentation.
                bias: candle === 'down' ? 'bullish' : candle === 'up' ? 'bearish' : 'neutral',
            };
            break;
        }
        if (!invalid)
            out.push(knot);
    }
    return out;
}
function direction(c) {
    return c.close > c.open ? 'up' : c.close < c.open ? 'down' : 'flat';
}
/**
 * Live knots nearest to a price, split by side.
 *
 * Knots above price are the levels a rally would settle into and knots below are
 * where a decline would; keeping them apart is what makes the set readable as
 * support and resistance rather than a cloud of lines.
 */
export function nearestKnots(knots, price, limit = 5) {
    const live = knots.filter((k) => k.hit === null);
    const above = live
        .filter((k) => k.level > price)
        .sort((a, b) => a.level - b.level)
        .slice(0, limit);
    const below = live
        .filter((k) => k.level <= price)
        .sort((a, b) => b.level - a.level)
        .slice(0, limit);
    return { above, below };
}
export function summariseKnots(knots) {
    const hits = knots.filter((k) => k.hit !== null);
    const total = knots.length;
    const ages = hits.map((k) => k.hit.barsLater).sort((a, b) => a - b);
    const mid = Math.floor(ages.length / 2);
    const median = ages.length === 0
        ? null
        : ages.length % 2 === 0
            ? (ages[mid - 1] + ages[mid]) / 2
            : ages[mid];
    return {
        total,
        hit: hits.length,
        live: total - hits.length,
        /*
         * Over the whole scanned range, not just resolved knots. The newest knots
         * have not had time to be taken, so the rate is a slight underestimate near
         * the right edge — reporting it against resolved knots only would instead
         * make it a guaranteed 100%, which is worse.
         */
        hitRate: total > 0 ? hits.length / total : null,
        medianBarsToHit: median,
        fromAbove: hits.filter((k) => k.hit.from === 'above').length,
        fromBelow: hits.filter((k) => k.hit.from === 'below').length,
        bullish: hits.filter((k) => k.hit.bias === 'bullish').length,
        bearish: hits.filter((k) => k.hit.bias === 'bearish').length,
    };
}
//# sourceMappingURL=knot.js.map